/**
 * Web stand-in for `react-native-reanimated`.
 *
 * The React Native Reusables components import Reanimated at module scope. Most
 * of those imports are only *applied* on native — `FadeIn`, `FadeOut`, and the
 * layout transitions are handed to `NativeOnlyAnimatedView`, which renders its
 * children untouched on web — but a few are evaluated on every platform:
 * the accordion rotates its chevron from a derived value, and `Animated.View`
 * is the plain container the primitives render through.
 *
 * Both apps in this workspace build for the web through Vite and
 * react-native-web, where Reanimated's Babel worklet toolchain is not wired in,
 * so this module stands in for it. Values settle at their target immediately
 * rather than animating: an accordion chevron still points the right way, and
 * CSS transitions declared in the Tailwind classes carry the motion that
 * matters.
 *
 * The animation props (`entering`, `exiting`, `layout`) are consumed here rather
 * than forwarded, because react-native-web would pass them through to the DOM.
 */

import * as React from 'react';
import { Pressable, View, type StyleProp, type ViewProps, type ViewStyle } from 'react-native';

/** Chainable placeholder for a Reanimated entering/exiting/layout animation. */
interface AnimationBuilder {
  duration(value: number): AnimationBuilder;
  delay(value: number): AnimationBuilder;
  reduceMotion(value: unknown): AnimationBuilder;
  withInitialValues(values: Record<string, unknown>): AnimationBuilder;
  build(): Record<string, never>;
}

function animation(): AnimationBuilder {
  const builder: AnimationBuilder = {
    duration: () => builder,
    delay: () => builder,
    reduceMotion: () => builder,
    withInitialValues: () => builder,
    build: () => ({}),
  };
  return builder;
}

/** Reanimated's reduced-motion policy. */
export const ReduceMotion = {
  System: 'system',
  Always: 'always',
  Never: 'never',
} as const;

/** Reanimated's clamp mode for `interpolate`. */
export const Extrapolation = { CLAMP: 'clamp', EXTEND: 'extend', IDENTITY: 'identity' } as const;

export const FadeIn = animation();
export const FadeInUp = animation();
export const FadeInDown = animation();
export const FadeOut = animation();
export const FadeOutUp = animation();
export const LinearTransition = animation();

/** Animation props that react-native-web must not receive. */
interface LayoutProps {
  entering?: unknown;
  exiting?: unknown;
  layout?: unknown;
}

/**
 * An animated style is whatever the caller's updater returned — a percentage
 * width, a rotation — which is not always structurally a React Native style.
 */
type AnimatedStyle = StyleProp<ViewStyle> | Record<string, unknown>;

/** Props of a view rendered through the shim. */
type AnimatedViewProps = Omit<ViewProps, 'style'> &
  LayoutProps & { children?: React.ReactNode; style?: AnimatedStyle };

/** Removes the Reanimated props, leaving a plain React Native view. */
function stripLayout<T extends LayoutProps>(props: T): Omit<T, keyof LayoutProps> {
  const { entering: _entering, exiting: _exiting, layout: _layout, ...rest } = props;
  return rest;
}

function AnimatedView({ children, style, ...props }: AnimatedViewProps) {
  return (
    <View style={style as StyleProp<ViewStyle>} {...stripLayout(props)}>
      {children}
    </View>
  );
}

/**
 * Renders its children; there is no layout animation to configure on web.
 *
 * @param props Children and Reanimated's skip flags, which are ignored.
 * @returns The children.
 */
export function LayoutAnimationConfig(props: {
  children?: React.ReactNode;
  skipEntering?: boolean;
  skipExiting?: boolean;
}): React.ReactNode {
  return <>{props.children}</>;
}

/**
 * Maps a value linearly from an input range onto an output range.
 *
 * @param value The value to map.
 * @param inputRange Source range, as `[start, end]`.
 * @param outputRange Target range, as `[start, end]`.
 * @param _extrapolation Clamp policy; this implementation always clamps.
 * @returns The mapped value.
 */
export function interpolate(
  value: number,
  inputRange: number[] = [0, 100],
  outputRange: number[] = [0, 100],
  _extrapolation?: unknown
): number {
  const [inStart = 0, inEnd = 100] = inputRange;
  const [outStart = 0, outEnd = 100] = outputRange;
  const span = inEnd - inStart;
  if (span === 0) return outStart;
  const ratio = Math.min(1, Math.max(0, (value - inStart) / span));
  return outStart + ratio * (outEnd - outStart);
}

/**
 * Returns the value without animating towards it.
 *
 * @param toValue The value to settle on.
 * @param _config Reanimated's timing configuration, which is ignored.
 * @returns The same value.
 */
export function withTiming<T>(toValue: T, _config?: unknown): T {
  return toValue;
}

/**
 * Returns the value without springing towards it.
 *
 * @param toValue The value to settle on.
 * @param _config Reanimated's spring configuration, which is ignored.
 * @returns The same value.
 */
export function withSpring<T>(toValue: T, _config?: unknown): T {
  return toValue;
}

/**
 * Computes the derived value on every render.
 *
 * @param updater The value factory.
 * @param _dependencies The factory's inputs, unused because React drives renders.
 * @returns A holder carrying the current value.
 */
export function useDerivedValue<T>(updater: () => T, _dependencies?: unknown[]): { value: T } {
  return { value: updater() };
}

/**
 * Computes the style on every render.
 *
 * @param updater The style factory.
 * @param _dependencies The factory's inputs, unused because React drives renders.
 * @returns The style object.
 */
export function useAnimatedStyle<T>(updater: () => T, _dependencies?: unknown[]): T {
  return updater();
}

/**
 * Wraps a component so it accepts Reanimated's animation props.
 *
 * @param Component The component to wrap.
 * @returns A component that drops the animation props before rendering.
 */
function createAnimatedComponent<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P & LayoutProps & { children?: React.ReactNode }> {
  function AnimatedComponent(props: P & LayoutProps & { children?: React.ReactNode }) {
    return <Component {...(stripLayout(props) as P)} />;
  }
  return AnimatedComponent;
}

const Animated = {
  View: AnimatedView,
  Pressable,
  createAnimatedComponent,
};

export default Animated;
